import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Slider from "react-slick";

// Hook to detect mobile and tablet
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Media types enum
const MediaType = {
  IMAGE: 'image',
  VIDEO: 'video',
  MULTI_POST: 'multi_post'
};

/**
 * Hook to calculate dynamic media height based on aspect ratio
 */
const useMediaHeight = (mediaHeight, mediaWidth, mediaType = MediaType.IMAGE) => {
  const [calculatedHeight, setCalculatedHeight] = useState(null);

  const calculateHeight = useCallback(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const containerWidth = getContainerWidth(screenWidth);

    const minHeight = 200;
    const maxHeightMap = {
      [MediaType.IMAGE]: screenHeight * 0.7,
      [MediaType.VIDEO]: screenHeight * 0.75,
      [MediaType.MULTI_POST]: screenHeight * 0.65
    };
    const maxHeight = maxHeightMap[mediaType] || screenHeight * 0.7;

    let height;
    if (mediaHeight && mediaWidth && mediaWidth > 0) {
      const aspectRatio = mediaHeight / mediaWidth;
      height = containerWidth * aspectRatio;
    } else {
      const defaultHeights = {
        [MediaType.IMAGE]: 400,
        [MediaType.VIDEO]: 500,
        [MediaType.MULTI_POST]: 400
      };
      height = defaultHeights[mediaType] || 400;
    }

    const clampedHeight = Math.max(minHeight, Math.min(height, maxHeight));
    setCalculatedHeight(clampedHeight);
  }, [mediaHeight, mediaWidth, mediaType]);

  useEffect(() => {
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, [calculateHeight]);

  return calculatedHeight;
};

/**
 * Calculate container width based on screen width
 */
const getContainerWidth = (screenWidth) => {
  if (screenWidth >= 1600) {
    return screenWidth - 280 - 380 - 40;
  } else if (screenWidth >= 1440) {
    return screenWidth - 240 - 320 - 40;
  } else if (screenWidth >= 1200) {
    return screenWidth - 220 - 300 - 40;
  } else if (screenWidth >= 1066) {
    return screenWidth - 200 - 280 - 40;
  } else if (screenWidth >= 992) {
    return screenWidth - 180 - 40;
  } else if (screenWidth >= 768) {
    return screenWidth - 70 - 20;
  } else if (screenWidth >= 480) {
    return screenWidth - 20;
  } else {
    return screenWidth - 16;
  }
};

/**
 * Feed Media Component - FIXED VERSION
 * 
 * FIXES:
 * 1. Proper slider height handling
 * 2. Video mute/unmute button with proper event handling
 * 3. Single image display for vendor details page
 */
const FeedMediaContent = ({ 
  post, 
  index,
  activeSlide,
  sliderSettings,
  initializeVideo,
  globalMute,
  toggleMute,
  videomute,
  videounmute,
  getActionTypeColor,
  showFullSlider = true, // NEW: Control whether to show full slider or just first image
  isVendorDetails = false, // NEW: Flag for vendor details page
  saveScrollPosition // NEW: Function to save scroll position before navigation
}) => {
  const isMobile = useIsMobile();
  
  // Get view_type from post (default to null if not present)
  const viewType = post.view_type;
  
  // Determine media type and dimensions (calculate these before hooks)
  const hasVideo = post.video_file && post.video_file.length > 0;
  const hasMultipleImages = post.post_images && post.post_images.length > 1;
  const hasSingleImage = post.post_images && 
                          post.post_images.length === 1 && 
                          post.post_images[0].post_images;

  let mediaType, mediaHeight, mediaWidth;
  let backgroundImage = null;

  // Handle view_type === 13: Image with background
  if (viewType === 13 && post.background_image) {
    backgroundImage = post.background_image;
  }

  if (hasVideo) {
    mediaType = MediaType.VIDEO;
    mediaHeight = post.video_height;
    mediaWidth = post.video_width;
  } else if (hasMultipleImages) {
    mediaType = MediaType.MULTI_POST;
    const firstImage = post.post_images[0];
    mediaHeight = firstImage.height;
    mediaWidth = firstImage.width;
  } else if (hasSingleImage) {
    mediaType = MediaType.IMAGE;
    const image = post.post_images[0];
    mediaHeight = image.height;
    mediaWidth = image.width;
  }

  // IMPORTANT: All hooks must be called before any conditional returns
  const calculatedHeight = useMediaHeight(mediaHeight, mediaWidth, mediaType);
  
  // Handle view_type === 11: Only description (no media) - AFTER hooks
  if (viewType === 11) {
    return null; // Return null to hide media section completely
  }

  // Check if original height > 1024px (tall image/video)
  const isTallMedia = mediaHeight && mediaHeight > 1024;
  
  // For mobile and tablet: use cover and no black background to display images/videos fully
  // For desktop: use contain for tall media or videos with black background
  const objectFit = isMobile 
    ? 'cover' 
    : (hasVideo ? 'contain' : (isTallMedia ? 'contain' : 'cover'));
  const backgroundColor = isMobile ? 'transparent' : ((hasVideo || isTallMedia) ? '#000' : 'transparent');

  // For view_type === 13, use background image
  const containerBackground = backgroundImage 
    ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: backgroundColor };

  return (
    <div 
      className="feed-image" 
      style={{ 
        height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
        ...containerBackground,
        position: 'relative',
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Multi-Image Post */}
      {hasMultipleImages ? (
        // FIXED: Show only first image for vendor details, full slider for home
        showFullSlider && !isVendorDetails ? (
          <div 
            className="multi-post-image" 
            style={{ 
              height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
              backgroundColor: backgroundColor,
              position: 'relative',
              width: '100%'
            }}
          >
            <div className="image-counter">
              {activeSlide + 1} / {post.post_images.length}
            </div>
            <Slider {...sliderSettings} style={{ height: '100%' }}>
              {post.post_images.map((img, idx) => {
                const imgIsTall = img.height && img.height > 1024;
                // For mobile and tablet: use cover and no black background
                const imgObjectFit = isMobile ? 'cover' : (imgIsTall ? 'contain' : 'cover');
                const imgBackgroundColor = isMobile ? 'transparent' : (imgIsTall ? '#000' : 'transparent');
                return (
                  <div 
                    className="slide-wrapper" 
                    key={idx} 
                    style={{ 
                      height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
                      width: '100%'
                    }}
                  >
                    <img
                      src={img.post_images}
                      alt={`Post ${post.id} Image ${idx + 1}`}
                      style={{ 
                        width: '100%', 
                        height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
                        objectFit: imgObjectFit,
                        display: 'block',
                        backgroundColor: imgBackgroundColor
                      }}
                    />
                  </div>
                );
              })}
            </Slider>
          </div>
        ) : (
          // Show only first image (for vendor details)
          (() => {
            const firstImage = post.post_images[0];
            const firstImgIsTall = firstImage.height && firstImage.height > 1024;
            const firstImgObjectFit = isMobile ? 'cover' : (firstImgIsTall ? 'contain' : 'cover');
            const firstImgBackgroundColor = isMobile ? 'transparent' : (firstImgIsTall ? '#000' : 'transparent');
            return (
              <img
                src={firstImage.post_images}
                alt={`Post ${post.id} Image 1`}
                style={{ 
                  width: '100%', 
                  height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
                  objectFit: firstImgObjectFit,
                  display: 'block',
                  backgroundColor: firstImgBackgroundColor
                }}
              />
            );
          })()
        )
      ) : hasSingleImage ? (
        /* Single Image Post - view_type === 12 or default image post */
        <img
          src={post.post_images[0].post_images}
          alt={`Post ${post.id} Image`}
          style={{ 
            width: '100%', 
            height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
            objectFit: objectFit,
            display: 'block'
          }}
        />
      ) : hasVideo ? (
        /* Video Post with Mute Button */
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <Link
            to="/videoposts"
            state={{ postData: post, postIndex: index }}
            onClick={() => {
              sessionStorage.setItem('videopost_navigated', 'true');
              sessionStorage.setItem('fetch_all_videos', 'true'); // Fetch all videos when clicking from feed
              if (saveScrollPosition && post?.id) saveScrollPosition(post.id);
            }}
            style={{ display: 'block', height: '100%', width: '100%' }}
          >
            <video
              ref={(el) => {
                if (el) {
                  initializeVideo(el, post.video_file, index);
                  // Set webkit-playsinline for older iOS versions
                  el.setAttribute('webkit-playsinline', 'true');
                }
              }}
              poster={post.video_cover_picture}
              autoPlay
              muted={globalMute}
              loop
              preload="metadata"
              disablePictureInPicture
              playsInline
              controls={false}
              style={{ 
                width: '100%', 
                height: calculatedHeight ? `${calculatedHeight}px` : 'auto',
                objectFit: objectFit,
                display: 'block'
              }}
            />
          </Link>
          
          {/* FIXED: Mute/Unmute Button with proper event handling */}
          <div 
            className="normal-video-post-mute-unmute-button" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleMute(e);
            }}
            style={{ cursor: 'pointer', zIndex: 20 }}
          >
            {globalMute ? (
              <img src={videomute} alt="video mute" title="Unmute" />
            ) : (
              <img src={videounmute} alt="video unmute" title="Mute" />
            )}
          </div>
        </div>
      ) : viewType === 13 && backgroundImage && !hasSingleImage && !hasMultipleImages ? (
        /* view_type === 13: Image with background only (no overlay image) - background already set on container */
        null
      ) : (
        /* No Media Placeholder */
        <img
          src="https://via.placeholder.com/400x200?text=No+Media"
          alt="No media available"
          style={{ 
            width: '100%', 
            height: 'auto',
            display: 'block'
          }}
        />
      )}

      {/* Action Type Badge */}
      {post.action_type && post.action_type.action_type_id && (
        post.action_type.website_url ? (
          <a
            href={post.action_type.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="remove-underline"
          >
            <div
              className="action-type-badge"
              style={{
                position: "absolute",
                bottom: "10px",
                right: "10px",
                padding: "5px 10px",
                backgroundColor: getActionTypeColor(post.action_type.action_type_id),
                color: "#fff",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "Roboto-Medium",
                zIndex: 10
              }}
            >
              {post.action_type.action_type.trim()}
            </div>
          </a>
        ) : post.action_type.link_type === "event" && post.event_id ? (
          <Link
            to={`/eventdetails/${post.event_id}`}
            className="remove-underline"
            onClick={saveScrollPosition}
          >
            <div
              className="action-type-badge"
              style={{ 
                backgroundColor: getActionTypeColor(post.action_type.action_type_id),
                position: "absolute",
                bottom: "10px",
                right: "10px",
                padding: "5px 10px",
                color: "#fff",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "Roboto-Medium",
                zIndex: 10
              }}
            >
              {post.action_type.action_type.trim()}
            </div>
          </Link>
        ) : post.action_type.link_type === "coupon" && post.coupon_id ? (
          <Link
            to={`/offers/${post.coupon_id}/0`}
            className="remove-underline"
            onClick={saveScrollPosition}
          >
            <div
              className="action-type-badge"
              style={{ 
                backgroundColor: getActionTypeColor(post.action_type.action_type_id),
                position: "absolute",
                bottom: "10px",
                right: "10px",
                padding: "5px 10px",
                color: "#fff",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "Roboto-Medium",
                zIndex: 10
              }}
            >
              {post.action_type.action_type.trim()}
            </div>
          </Link>
        ) : post.coupon_id ? (
          <Link
            to={`/offers/${post.coupon_id}/0`}
            className="remove-underline"
            onClick={saveScrollPosition}
          >
            <div
              className="action-type-badge"
              style={{ 
                backgroundColor: getActionTypeColor(post.action_type.action_type_id),
                position: "absolute",
                bottom: "10px",
                right: "10px",
                padding: "5px 10px",
                color: "#fff",
                borderRadius: "4px",
                fontSize: "13px",
                fontFamily: "Roboto-Medium",
                zIndex: 10
              }}
            >
              {post.action_type.action_type.trim()}
            </div>
          </Link>
        ) : null
      )}
    </div>
  );
};

// Export
export { 
  useMediaHeight, 
  MediaType,
  FeedMediaContent
};